import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Send, Trash2 } from "lucide-react";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";

const memoryDate = (value) => new Intl.DateTimeFormat("en", {
  month: "long",
  day: "numeric",
  year: "numeric",
}).format(new Date(value));

function MemorialPortrait({ person, large = false }) {
  const [missing, setMissing] = useState(false);
  const initials = `${person.firstName?.[0] || ""}${person.lastName?.[0] || ""}`;

  return (
    <div className={large ? "memorial-portrait is-large" : "memorial-portrait"}>
      {person.photoUrl && !missing ? (
        <img
          src={person.photoUrl}
          alt={`College portrait of ${person.name}`}
          onError={() => setMissing(true)}
        />
      ) : (
        <span aria-label={person.name}>{initials}</span>
      )}
    </div>
  );
}

function MemoryList({ person, onRemove, removing }) {
  return (
    <div className="memorial-memory-list">
      {person.memories.length === 0 && (
        <div className="memorial-memory-empty">
          <Heart aria-hidden="true" />
          <p>No memories have been shared here yet.</p>
          <span>A story, a kindness, or a small moment can be a beautiful beginning.</span>
        </div>
      )}
      {person.memories.map((memory, index) => (
        <article className="memorial-memory" key={memory.id}>
          <span className="memorial-memory__number">{String(index + 1).padStart(2, "0")}</span>
          <div>
            <p>{memory.body}</p>
            <footer>
              <div>
                <strong>{memory.author.name}</strong>
                <span>{memoryDate(memory.createdAt)}</span>
              </div>
              {memory.canDelete && (
                <button
                  className="memorial-memory__remove"
                  type="button"
                  disabled={removing === memory.id}
                  onClick={() => onRemove(memory)}
                >
                  <Trash2 aria-hidden="true" />
                  {removing === memory.id ? "Removing…" : "Remove"}
                </button>
              )}
            </footer>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function Memorials({ session, onLogin }) {
  const authenticated = Boolean(session?.authenticated);
  const [selectedId, setSelectedId] = useState(null);
  const [memory, setMemory] = useState("");
  const [actionError, setActionError] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState("");
  const loader = useCallback(
    () => authenticated ? api("/api/memorials") : Promise.resolve({ people: [], canContribute: false }),
    [authenticated],
  );
  const { data, error, loading, setData } = useApi(loader, [loader]);
  const people = useMemo(() => data?.people || [], [data]);

  useEffect(() => {
    if (people.length && !people.some((person) => person.memberId === selectedId)) {
      setSelectedId(people[0].memberId);
    }
  }, [people, selectedId]);

  const selected = people.find((person) => person.memberId === selectedId) || people[0];
  const canContribute = Boolean(data?.canContribute);

  function selectPerson(memberId) {
    setSelectedId(memberId);
    setMemory("");
    setActionError("");
    setStatus("");
    window.requestAnimationFrame(() => {
      document.getElementById("memorial-tribute")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function shareMemory(event) {
    event.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    setActionError("");
    setStatus("");
    try {
      const result = await api(`/api/memorials/${selected.memberId}/memories`, {
        method: "POST",
        body: JSON.stringify({ body: memory }),
      });
      setData((current) => ({
        ...current,
        people: current.people.map((person) => person.memberId === selected.memberId
          ? { ...person, memories: [...person.memories, result.memory] }
          : person),
      }));
      setMemory("");
      setStatus(`Your memory of ${selected.name} is now part of this tribute.`);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function removeMemory(item) {
    if (!selected || !window.confirm("Remove this memory from the tribute?")) return;
    setRemoving(item.id);
    setActionError("");
    setStatus("");
    try {
      await api(`/api/memorials/${selected.memberId}/memories/${item.id}`, { method: "DELETE" });
      setData((current) => ({
        ...current,
        people: current.people.map((person) => person.memberId === selected.memberId
          ? { ...person, memories: person.memories.filter((candidate) => candidate.id !== item.id) }
          : person),
      }));
      setStatus("The memory was removed.");
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setRemoving("");
    }
  }

  if (!authenticated) {
    return <div className="memorial-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }

  return (
    <>
      <section className="memorial-hero">
        <div className="memorial-hero__year" aria-hidden="true">95</div>
        <div className="memorial-hero__copy">
          <p className="kicker">In memoriam · MACE 1995</p>
          <h1>Absent from the room.<br /><em>Present in every story.</em></h1>
          <p>We remember the classmates who left us too soon. Their place in our class remains—in the friendships they shaped, the days we shared, and the stories we still carry.</p>
        </div>
        <blockquote>
          <span aria-hidden="true">“</span>
          <p>To live in hearts we leave behind is not to die.</p>
          <cite>Thomas Campbell</cite>
        </blockquote>
      </section>

      <section className="memorial-roster section-wrap" aria-labelledby="memorial-roster-title">
        <header>
          <div>
            <p className="kicker">In loving memory</p>
            <h2 id="memorial-roster-title">Forever part of our class</h2>
          </div>
          <span>{people.length} classmates remembered</span>
        </header>
        {loading && <Loading label="Opening the memorial" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!loading && !error && people.length === 0 && (
          <p className="memorial-roster__empty">No memorial records are available yet.</p>
        )}
        <div className="memorial-roster__grid">
          {people.map((person, index) => (
            <button
              className={person.memberId === selected?.memberId ? "memorial-person is-selected" : "memorial-person"}
              type="button"
              key={person.memberId}
              aria-pressed={person.memberId === selected?.memberId}
              onClick={() => selectPerson(person.memberId)}
            >
              <span className="memorial-person__index">{String(index + 1).padStart(2, "0")}</span>
              <MemorialPortrait person={person} />
              <span className="memorial-person__name">{person.name}</span>
              <span className="memorial-person__meta">{person.branch || "MACE ’95"}{person.years ? ` · ${person.years}` : ""}</span>
              <span className="memorial-person__prompt">Read & remember</span>
            </button>
          ))}
        </div>
      </section>

      {selected && (
        <section className="memorial-tribute" id="memorial-tribute" aria-labelledby="memorial-tribute-title">
          <div className="section-wrap memorial-tribute__inner">
            <aside className="memorial-tribute__person">
              <p className="kicker">A place to remember</p>
              <MemorialPortrait person={selected} large />
              <h2 id="memorial-tribute-title">{selected.name}</h2>
              <p className="memorial-tribute__meta">{selected.branch || "MACE ’95"}{selected.years ? ` · ${selected.years}` : ""}</p>
              <p className="memorial-tribute__note">Share the moment that comes back first—their laugh, a kindness, a classroom story, or simply what their friendship meant to you.</p>
            </aside>

            <div className="memorial-tribute__memories">
              <header>
                <div>
                  <p className="kicker">Memories from classmates</p>
                  <h2>What we remember</h2>
                </div>
                <span><MessageCircle aria-hidden="true" /> {selected.memories.length}</span>
              </header>

              {canContribute ? (
                <form className="memorial-memory-form" onSubmit={shareMemory}>
                  <label htmlFor="memorial-memory">Share a memory of {selected.firstName}</label>
                  <textarea
                    id="memorial-memory"
                    required
                    maxLength="2000"
                    rows="5"
                    value={memory}
                    onChange={(event) => setMemory(event.target.value)}
                    placeholder="Write a memory in your own words…"
                  />
                  <div>
                    <small>{memory.length.toLocaleString()} / 2,000</small>
                    <button className="button button--yellow" disabled={submitting || !memory.trim()} type="submit">
                      {submitting ? "Sharing…" : "Share this memory"} <Send aria-hidden="true" />
                    </button>
                  </div>
                </form>
              ) : (
                <p className="memorial-account-note">
                  You’re viewing with the shared class passphrase. <Link to="/members">Use your individual account</Link> to share a memory.
                </p>
              )}

              <div className="memorial-action-status" aria-live="polite">
                {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
                {status && <p>{status}</p>}
              </div>
              <MemoryList person={selected} onRemove={removeMemory} removing={removing} />
            </div>
          </div>
        </section>
      )}
    </>
  );
}
